import { DateTime } from "luxon";

import { isBlackoutInstant } from "./blackouts/blackoutHelpers";

export const SLOT_STEP_MINUTES = 15;

export const PICKUP_OFFSETS = [
  { key: "NOW", label: "Now", minutes: 0 },
  { key: "IN_5", label: "In 5 min", minutes: 5 },
  { key: "IN_15", label: "In 15 min", minutes: 15 },
  { key: "IN_30", label: "In 30 min", minutes: 30 },
  { key: "LATER_TODAY", label: "Later today", minutes: null },
  { key: "TOMORROW", label: "Tomorrow", minutes: null },
];

/** The chips that open the slot grid rather than naming a time themselves. */
export const PICKS_A_DAY = ["LATER_TODAY", "TOMORROW"];

/**
 * Is there anybody on to collect a car at `iso`?
 */
export function withinCollections(iso, windows = []) {
  if (!iso) return false;
  if (!windows.length) return true;

  const at = DateTime.fromISO(iso).toMillis();

  return windows.some(
    ({ startsAt, endsAt }) =>
      at >= DateTime.fromISO(startsAt).toMillis() &&
      at < DateTime.fromISO(endsAt).toMillis(),
  );
}

/**
 * Can the car be collected at `iso`? Somebody has to be rostered for it, and
 * the booking has to still be open: the card expires a set number of hours
 * after the stay ends, and nothing fetches a car past that.
 */
export function isCollectable(
  iso,
  { windows = [], expiresAt = null, blackouts = [], timezone } = {},
) {
  if (!iso) return false;
  if (!withinCollections(iso, windows)) return false;
  if (blackouts.some((period) => isBlackoutInstant(period, iso, timezone))) {
    return false;
  }
  if (!expiresAt) return true;

  return DateTime.fromISO(iso) <= DateTime.fromISO(expiresAt);
}

/*
 * The clock the grid is built from, in venue time. The server's when the valet
 * endpoint gave us one - the desk machine's is only a fallback, and a minute of
 * drift on it costs nothing the server's own check will not catch.
 */
function venueNow(now, timezone) {
  const dt = now
    ? DateTime.fromISO(now, { zone: timezone })
    : DateTime.now().setZone(timezone);

  return dt.isValid ? dt : null;
}

/** The first slot boundary strictly after `from`. */
function nextBoundary(from, stepMinutes) {
  return from
    .set({ second: 0, millisecond: 0 })
    .set({ minute: Math.floor(from.minute / stepMinutes) * stepMinutes })
    .plus({ minutes: stepMinutes });
}

/**
 * The slots left on a venue day. Times already gone are simply absent, while
 * hours nobody is rostered for stay on, struck through, because the desk is
 * owed the reason a time it can see is one it cannot book.
 */
export function pickupSlots({
  now,
  timezone,
  day = "TODAY",
  stepMinutes = SLOT_STEP_MINUTES,
  ...limits
}) {
  const current = venueNow(now, timezone);
  if (!current) return [];

  const target = day === "TOMORROW" ? current.plus({ days: 1 }) : current;
  const endOfDay = target.endOf("day");
  /* Tomorrow opens at midnight; today opens at the next step still ahead. */
  const first =
    day === "TOMORROW"
      ? target.startOf("day")
      : nextBoundary(current, stepMinutes);

  const slots = [];

  for (
    let slot = first;
    slot <= endOfDay;
    slot = slot.plus({ minutes: stepMinutes })
  ) {
    const iso = slot.toUTC().toISO();

    const blackout = (limits.blackouts ?? []).some((period) =>
      isBlackoutInstant(period, iso, timezone),
    );

    slots.push({
      iso,
      label: slot.toFormat("h:mm a"),
      available: isCollectable(iso, { ...limits, timezone }),
      ...(blackout ? { blackout: true } : {}),
    });
  }

  return slots;
}

/** The instant `minutes` from now, for the fixed offset chips. */
export function offsetIso(now, timezone, minutes) {
  const current = venueNow(now, timezone);
  if (!current) return null;

  return current
    .plus({ minutes })
    .set({ second: 0, millisecond: 0 })
    .toUTC()
    .toISO();
}

/**
 * A booked time, as the desk reads it back: the clock face and the day it
 * falls on, because "8:00 AM" alone is two different pickups.
 */
export function formatWhen(iso, timezone, now) {
  if (!iso) return "";

  const dt = DateTime.fromISO(iso, { zone: timezone });
  if (!dt.isValid) return "";

  const clock = dt.toFormat("h:mm a");
  const current = venueNow(now, timezone);
  if (!current) return clock;

  /* The grid offers today and tomorrow only, so those are the only two words a
   * booked time can carry - including a fixed offset that rolls past midnight. */
  const tomorrow = dt.startOf("day") > current.startOf("day");

  return `${tomorrow ? "Tomorrow" : "Today"} ${clock}`;
}
